import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { LuFileWarning } from "react-icons/lu";
import { FiSearch } from "react-icons/fi";

import ListPageShell from "../list-page/ListPageShell";
import ListPageHeader from "../list-page/ListPageHeader";
import ListPageSearch from "../list-page/ListPageSearch";
import ReceiptTypeFilter from "../list-page/ReceiptTypeFilter";
import ListPageEmpty from "../list-page/ListPageEmpty";
import ListPager from "../list-page/ListPager";

import ReceiptTable from "./ReceiptTable";
import ReactModal from "../ReactModal";
import ReceiptView from "../ReceiptView";
import { useGetFlaggedReceiptsQuery } from "../../store/services/receiptsServices";
import { receiptViewActions } from "../../store/receiptViewSlice";

const PAGE_LIMIT = 10;

export const FlaggedReceipts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isReceiptViewOpen = useSelector((s) => s.receipt_view.isOpen);

  const [filteredFlaggedReceipts, setFilteredFlaggedReceipts] = useState([]);
  const [pages, setPages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [params, setParams] = useState({});
  const [searchTextInput, setSearchTextInput] = useState("");
  const [query] = useState(new URLSearchParams(window.location.search));

  const { data, isLoading, isFetching } = useGetFlaggedReceiptsQuery({ params });

  /* Hydrate from URL */
  useEffect(() => {
    const newParams = {};
    query.forEach((v, k) => (newParams[k] = v));
    setParams(newParams);
    if (query.has("page")) setCurrentPage(parseInt(query.get("page")));
    if (query.has("q")) setSearchTextInput(query.get("q"));
  }, [query]);

  /* Sync data */
  useEffect(() => {
    if (data) {
      setFilteredFlaggedReceipts(data?.data?.data || []);
      setPages({
        currentPage: data?.data?.currentPage,
        totalPages: data?.data?.totalPages,
        totalCount: data?.data?.totalCount,
      });
    }
  }, [data]);

  /* Reflect params into URL */
  useEffect(() => {
    const urlParams = new URLSearchParams(params).toString();
    navigate(`/flagged_receipts?${urlParams}`, { replace: true });
  }, [params, navigate]);

  /* Search debounce */
  const debounceRef = useRef(null);
  const handleSearchTextInputChange = (e) => {
    const text = e.target.value;
    setSearchTextInput(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.length > 2) {
      debounceRef.current = setTimeout(() => {
        setParams((p) => ({ ...p, q: text, page: 1 }));
        setCurrentPage(1);
      }, 400);
    } else {
      setParams((p) => {
        const { q, ...rest } = p;
        return rest;
      });
    }
  };

  const handleSort = (value) => {
    setParams((p) => {
      if (p.sort_by === value && p.sort_order === "asc") {
        const { sort_by, sort_order, ...rest } = p;
        return rest;
      }
      return {
        ...p,
        sort_by: value,
        sort_order: p.sort_by === value && p.sort_order === "desc" ? "asc" : "desc",
      };
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setPages((p) => ({ ...p, currentPage: page }));
    setParams((p) => ({ ...p, page }));
  };

  const handleRowClick = (receipt) => {
    dispatch(receiptViewActions.openModal(receipt));
  };

  const handleTypeChange = (type) => {
    setCurrentPage(1);
    setParams((p) => {
      const { type: _, page, ...rest } = p;
      return type ? { ...rest, type } : rest;
    });
  };

  const hasFilters = !!params.q || !!params.type;

  const handleClearFilters = () => {
    setSearchTextInput("");
    setCurrentPage(1);
    setParams({});
  };

  const isEmpty =
    !isLoading && !isFetching && filteredFlaggedReceipts.length === 0;

  const totalCount = pages?.totalCount ?? 0;
  const rangeLabel = useMemo(() => {
    if (!totalCount) return "";
    const start = (currentPage - 1) * PAGE_LIMIT + 1;
    const end = Math.min(currentPage * PAGE_LIMIT, totalCount);
    return `Po shfaqen ${start}–${end} prej ${totalCount.toLocaleString()}`;
  }, [currentPage, totalCount]);

  return (
    <>
      {isReceiptViewOpen && <ReceiptView />}
      <ReactModal />

      <ListPageShell>
        <ListPageHeader
          eyebrow={{ icon: <LuFileWarning size={18} />, label: "Kuponat e parregullt" }}
          title="Kuponat e parregullt"
          subtitle={
            totalCount
              ? `${totalCount.toLocaleString()} kupona të parregullt`
              : "Lista e kuponave të parregullt"
          }
          info="Kuponat e parregullt janë kupona PEF të cilët nuk janë gjetur në bazë të numrit serik të dhënë."
        />

        <ListPageSearch
          value={searchTextInput}
          onChange={handleSearchTextInputChange}
          placeholder="Kërko sipas ID-së ose përdoruesit..."
          extras={<ReceiptTypeFilter value={params.type || ""} onChange={handleTypeChange} />}
        />

        {isEmpty ? (
          <ListPageEmpty
            icon={hasFilters ? FiSearch : LuFileWarning}
            title={hasFilters ? "Asnjë kupon nuk u gjet" : "Asnjë kupon i parregullt"}
            subtitle={
              hasFilters
                ? "Provoni të ndryshoni ose të pastroni filtrat për të parë më shumë rezultate."
                : "Kuponat e parregullt do të shfaqen këtu."
            }
            action={
              hasFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
                >
                  Pastro filtrat
                </button>
              )
            }
          />
        ) : (
          <ReceiptTable
            receipts={filteredFlaggedReceipts}
            isLoading={isLoading || isFetching}
            params={params}
            onSort={handleSort}
            onRowClick={handleRowClick}
          />
        )}

        {!isEmpty && (
          <ListPager
            currentPage={currentPage}
            totalPages={pages?.totalPages || 1}
            onChange={handlePageChange}
            rangeLabel={rangeLabel}
          />
        )}
      </ListPageShell>
    </>
  );
};
